import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProvenanceBadge } from "@/components/Badge";
import { Loader2, Calendar, Check, Lock, Ticket, Video, Phone } from "lucide-react";
import { toast } from "sonner";
import SlotPicker from "@/components/SlotPicker";

export default function Booking() {
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [current, setCurrent] = useState(null);
  const [credits, setCredits] = useState(0);
  const [plan, setPlan] = useState(user?.plan);
  const [canModify, setCanModify] = useState(true);
  const [windowH, setWindowH] = useState(24);
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(false);
  const navigate = useNavigate();

  const loadCurrent = () => api.get("/booking/current").then((r) => {
    setCurrent(r.data?.booking?.booking_id ? r.data.booking : null);
    setCredits(r.data?.rdv_credits || 0);
    setPlan(r.data?.plan);
    setCanModify(r.data?.can_modify !== false);
    setWindowH(r.data?.cancellation_window_hours || 24);
    if (r.data?.phone) setPhone(r.data.phone);
  }).catch(() => {});

  const [rdvPrice, setRdvPrice] = useState(null);
  useEffect(() => {
    api.get("/booking/slots").then((r) => setSlots(r.data.slots));
    api.get("/plans").then((r) => setRdvPrice((r.data.plans || []).find((p) => p.key === "rdv_unique")?.price_display || null)).catch(() => {});
    loadCurrent();
  }, []);

  const premium = plan === "premium";
  const canBook = premium || credits > 0 || !!current;

  const schedule = async () => {
    if (!selected) return;
    if (!phone.trim()) { toast.error("Merci d'indiquer un numéro où te joindre"); return; }
    setLoading(true);
    try {
      // Si un RDV existe déjà -> reprogrammation (sans consommer de crédit) ; sinon réservation via crédit.
      const endpoint = current ? "/booking/reschedule" : "/booking/schedule";
      const r = await api.post(endpoint, { slot_id: selected.id, slot_label: selected.label, phone: phone.trim(), note: note.trim() });
      setCurrent(r.data); setSelected(null);
      toast.success(current ? "Rendez-vous reprogrammé" : "Rendez-vous confirmé");
      loadCurrent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Annuler ce rendez-vous ? Le créneau sera libéré et un crédit te sera recrédité.")) return;
    setLoading(true);
    try {
      await api.post("/booking/cancel");
      setCurrent(null); setSelected(null);
      toast.success("Rendez-vous annulé — un crédit t'a été recrédité");
      api.get("/booking/slots").then((r) => setSlots(r.data.slots));
      loadCurrent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const buyRdv = async () => {
    setBuying(true);
    try {
      const r = await api.post("/payments/checkout", { plan: "rdv_unique", origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch { toast.error("Erreur de paiement"); setBuying(false); }
  };

  if (!canBook) {
    return (
      <ClientLayout>
        <div className="fade-up max-w-lg pt-8 text-center">
          <span className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mx-auto"><Lock size={20} className="text-obsidian/50" /></span>
          <h1 className="mt-6 text-3xl font-light tracking-tight">Rendez-vous avec Ton Coach</h1>
          <p className="mt-3 text-obsidian/60">Le suivi humain est inclus dans l'offre Premium. Avec l'Essentiel, tu peux réserver un rendez-vous unique à la carte.</p>
          <div className="mt-8 grid gap-3">
            <button data-testid="buy-rdv-unique" onClick={buyRdv} disabled={buying}
              className="rounded-full bg-obsidian text-white px-7 py-4 font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {buying ? <Loader2 size={17} className="animate-spin" /> : <Ticket size={17} />} Rendez-vous à la carte{rdvPrice ? ` · ${rdvPrice}` : ""}
            </button>
            <button data-testid="upgrade-premium" onClick={() => navigate("/tarifs")}
              className="rounded-full border border-obsidian px-7 py-4 font-medium hover:bg-obsidian hover:text-white transition-colors">Passer au Premium</button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="fade-up max-w-xl">
        <ProvenanceBadge type="coach">{premium ? "SUIVI HUMAIN · PREMIUM" : "RENDEZ-VOUS À LA CARTE"}</ProvenanceBadge>
        <h1 className="mt-5 text-3xl font-light tracking-tight">Réserver avec Ton Coach</h1>
        <p className="mt-3 text-obsidian/60 leading-relaxed">
          {premium ? "Le rendez-vous mensuel : écoute, recul, motivation et grandes orientations. Inclus dans ton offre Premium."
                   : `Tu disposes de ${credits} crédit(s) de rendez-vous. Choisis un créneau ouvert par Ton Coach.`}
        </p>

        {current && (
          <div className="mt-6 rounded-xl border border-sage/30 bg-sage/8 p-5">
            <div className="flex items-center gap-3">
              <Check size={18} className="text-sage" />
              <div className="text-sm">Rendez-vous confirmé — <span className="font-medium">{current.slot_label}</span>
                {current.mode === "phone" && <span className="ml-2 inline-flex items-center gap-1 text-obsidian/60"><Phone size={13} /> par téléphone</span>}
              </div>
            </div>
            {current.mode === "phone" ? (
              <p data-testid="booking-phone-note" className="mt-3 text-sm text-obsidian/70 flex items-center gap-2"><Phone size={15} /> Ton Coach t'appellera au créneau choisi.</p>
            ) : current.meet_link ? (
              <a data-testid="booking-meet-link" href={current.meet_link} target="_blank" rel="noreferrer"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-4 py-2 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                <Video size={15} /> Rejoindre la visio (Google Meet)
              </a>
            ) : (
              <p className="mt-3 text-sm text-obsidian/60 flex items-center gap-2"><Video size={15} /> Le lien visio te sera envoyé par email et ajouté à ton agenda.</p>
            )}
            <button data-testid="booking-cancel" onClick={cancel} disabled={loading || !canModify}
              className="mt-4 text-sm text-red-600 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed">Annuler ce rendez-vous</button>
            {!canModify && <p className="mt-1.5 text-xs text-obsidian/45">Annulation/reprogrammation impossible à moins de {windowH}h du rendez-vous — contacte Ton Coach directement.</p>}
          </div>
        )}

        {!(current && !canModify) && (
          <>
            <h2 className="mt-8 text-sm tracking-[0.16em] uppercase font-semibold text-obsidian/50 flex items-center gap-2"><Calendar size={15} /> {current ? "Reprogrammer" : "Créneaux disponibles"}</h2>
            {slots.length === 0 ? (
              <p className="mt-4 text-sm text-obsidian/40">Aucun créneau ouvert pour le moment. Ton Coach ouvrira bientôt de nouvelles disponibilités.</p>
            ) : (
              <div className="mt-4"><SlotPicker slots={slots} selected={selected} onSelect={setSelected} testidPrefix="slot" /></div>
            )}
          </>
        )}

        {!(current && !canModify) && (
          <>
            <div className="mt-6 space-y-3" data-testid="booking-contact">
              <div>
                <label className="block text-sm font-medium text-obsidian/70 mb-1">Un numéro où te joindre {selected?.mode === "phone" ? "(Ton Coach t'appellera)" : "(au cas où)"}</label>
                <input data-testid="booking-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="06 12 34 56 78" className="w-full rounded-lg border border-border bg-white px-4 py-3 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-obsidian/70 mb-1">Un mot pour Ton Coach (optionnel)</label>
                <textarea data-testid="booking-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Ce que tu aimerais aborder, une contrainte, une question…" className="w-full rounded-lg border border-border bg-white px-4 py-3 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
              </div>
            </div>

            <button data-testid="schedule-button" onClick={schedule} disabled={!selected || loading}
              className="mt-6 w-full rounded-full bg-obsidian text-white py-4 font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-40">
              {loading ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />}
              {selected ? (current ? `Reprogrammer · ${selected.label}` : `Confirmer · ${selected.label}`) : "Sélectionne un créneau"}
            </button>
          </>
        )}
      </div>
    </ClientLayout>
  );
}
